/**
 * Client-side Countdown Timer with localStorage persistence
 * Allows countdown to persist across pages and browser sessions
 */

if (!customElements.get('m-client-countdown-timer')) {
  class ClientCountdownTimer extends HTMLElement {
    constructor() {
      super();
      this.storageKey = '';
      this.hours = 2; // Default 2 hours
      this.countdownTimer = null;
    }

    connectedCallback() {
      // Get hours and storage key from data attributes
      this.hours = parseInt(this.dataset.hours) || 2;
      this.storageKey = this.dataset.storageKey || 'countdown_timer';
      
      // Initialize the countdown
      this.initCountdown();
    }

    initCountdown() {
      const now = Date.now();
      let endTime = this.getStoredEndTime();

      // If no stored end time or it's expired, create a new countdown
      if (!endTime || endTime <= now) {
        // Calculate end time: current time + hours in milliseconds
        endTime = now + (this.hours * 60 * 60 * 1000);
        this.saveEndTime(endTime);
      }

      // Start the countdown timer
      if (window.MinimogTheme && window.MinimogTheme.CountdownTimer) {
        this.countdownTimer = new window.MinimogTheme.CountdownTimer(
          this,
          now,
          endTime,
          {
            addZeroPrefix: true,
            loop: false,
            callback: () => {
              // When countdown ends, remove from storage and optionally hide
              this.clearStorage();
              this.handleExpired();
            }
          }
        );

        // Show the timer
        this.style.opacity = '1';
        this.classList.remove('hidden');
        this.classList.remove('m:hidden');
      } else {
        console.error('CountdownTimer class not found. Make sure theme-global.js is loaded.');
      }
    }

    /**
     * Get stored end time from localStorage
     * @returns {number|null} End time in milliseconds or null if not found
     */
    getStoredEndTime() {
      try {
        const stored = localStorage.getItem(this.storageKey);
        if (stored) {
          const endTime = parseInt(stored);
          return isNaN(endTime) ? null : endTime;
        }
      } catch (e) {
        console.error('Error reading from localStorage:', e);
      }
      return null;
    }

    /**
     * Save end time to localStorage
     * @param {number} endTime - End time in milliseconds
     */
    saveEndTime(endTime) {
      try {
        localStorage.setItem(this.storageKey, endTime.toString());
      } catch (e) {
        console.error('Error writing to localStorage:', e);
      }
    }

    /**
     * Clear stored end time from localStorage
     */
    clearStorage() {
      try {
        localStorage.removeItem(this.storageKey);
      } catch (e) {
        console.error('Error removing from localStorage:', e);
      }
    }

    /**
     * Handle when countdown expires
     */
    handleExpired() {
      // Hide the countdown timer
      const timerWrapper = this.querySelector('.m-countdown-timer__active');
      if (timerWrapper) {
        timerWrapper.style.display = 'none';
      }
      
      // Show expired message
      const expiredContainer = this.querySelector('.m-countdown-timer__expired');
      const expiredMessage = this.querySelector('.m-countdown-timer__expired-message');
      const messageText = this.dataset.expiredMessage || 'This offer has expired';
      
      if (expiredMessage && expiredContainer) {
        expiredMessage.textContent = messageText;
        expiredContainer.style.display = 'block';
      }
      
      this.classList.add('m-countdown-expired');
      
      // Dispatch custom event that parent section can listen to
      this.dispatchEvent(new CustomEvent('countdown-expired', {
        bubbles: true,
        detail: { storageKey: this.storageKey }
      }));
    }

    /**
     * Reset the countdown (can be called externally)
     */
    reset() {
      this.clearStorage();
      if (this.countdownTimer) {
        this.countdownTimer.clear();
      }
      this.initCountdown();
    }

    disconnectedCallback() {
      // Clean up if needed
      if (this.countdownTimer) {
        this.countdownTimer.clear();
      }
    }
  }

  customElements.define('m-client-countdown-timer', ClientCountdownTimer);
}
